import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { insertCarrierSchema } from "@shared/schema";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Download, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const formSchema = insertCarrierSchema.extend({
  name: z.string().min(1, "Carrier name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().optional(),
  address: z.string().optional(),
  docketPrefix: z.enum(["MC", "FF", "MX"]),
  docketNumber: z.string().optional(),
  companyId: z.number().min(1, "Please select a company"),
});

type FormData = z.infer<typeof formSchema>;

interface CreateCarrierModalProps {
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  companies: any[];
  defaultCompanyId?: number;
}

export default function CreateCarrierModal({
  onClose,
  onSubmit,
  isLoading,
  companies,
  defaultCompanyId,
}: CreateCarrierModalProps) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      docketPrefix: "MC" as const,
      docketNumber: "",
      companyId: defaultCompanyId || 0,
    },
  });

  const selectedCompanyId = watch("companyId");
  const selectedDocketPrefix = watch("docketPrefix");
  const docketNumber = watch("docketNumber");
  const carrierName = watch("name");
  const carrierEmail = watch("email");
  const carrierPhone = watch("phone");
  const carrierAddress = watch("address");

  const fetchFMCSADataMutation = useMutation({
    mutationFn: async () => {
      const docketPrefix = getValues('docketPrefix');
      const docketNumber = getValues('docketNumber');
      
      if (!docketNumber) {
        throw new Error("Docket number is required to fetch FMCSA data");
      }
      
      const response = await apiRequest("POST", "/api/fmcsa/fetch-by-docket", {
        docketPrefix,
        docketNumber,
      });
      
      // Parse the JSON response
      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      console.log('[FMCSA Fetch] Success! Received data:', data);
      
      // Update specific fields with fetched FMCSA data using setValue with trigger
      if (data.name) {
        console.log('[FMCSA Fetch] Setting name to:', data.name);
        setValue('name', data.name, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
      if (data.email) {
        setValue('email', data.email, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
      if (data.phone) {
        setValue('phone', data.phone, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
      if (data.address) {
        setValue('address', data.address, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
      if (data.docketPrefix) {
        setValue('docketPrefix', data.docketPrefix, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
      if (data.docketNumber) {
        setValue('docketNumber', data.docketNumber, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
      
      toast({
        title: "Success",
        description: "Carrier data loaded from FMCSA. Review and submit the form.",
      });
    },
    onError: (error: any) => {
      console.error('[FMCSA Fetch] Error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch FMCSA data",
        variant: "destructive",
      });
    },
  });

  const handleFetchFMCSAData = () => {
    console.log('[FMCSA Fetch] Button clicked, triggering mutation...');
    fetchFMCSADataMutation.mutate();
  };

  const handleFormSubmit = (data: FormData) => {
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Add New Carrier</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Carrier Name</Label>
              <Input
                id="name"
                placeholder="Enter carrier name"
                value={carrierName}
                onChange={(e) => setValue("name", e.target.value, { shouldValidate: true, shouldDirty: true })}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Select
                value={selectedCompanyId > 0 ? selectedCompanyId.toString() : ""}
                onValueChange={(value) => setValue("companyId", parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a company..." />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id.toString()}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.companyId && (
                <p className="text-sm text-red-600">{errors.companyId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter carrier email"
                value={carrierEmail}
                onChange={(e) => setValue("email", e.target.value, { shouldValidate: true, shouldDirty: true })}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="Enter phone number"
                value={carrierPhone}
                onChange={(e) => setValue("phone", e.target.value, { shouldValidate: true, shouldDirty: true })}
              />
              {errors.phone && (
                <p className="text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                placeholder="Enter carrier address"
                value={carrierAddress}
                onChange={(e) => setValue("address", e.target.value, { shouldValidate: true, shouldDirty: true })}
                rows={2}
              />
              {errors.address && (
                <p className="text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>



            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="docketPrefix">Docket Prefix</Label>
                  <Select
                    value={selectedDocketPrefix}
                    onValueChange={(value) => setValue("docketPrefix", value as "MC" | "FF" | "MX")}
                  >
                    <SelectTrigger data-testid="select-docket-prefix">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MC">MC</SelectItem>
                      <SelectItem value="FF">FF</SelectItem>
                      <SelectItem value="MX">MX</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.docketPrefix && (
                    <p className="text-sm text-red-600">{errors.docketPrefix.message}</p>
                  )}
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="docketNumber">Docket Number</Label>
                  <Input
                    id="docketNumber"
                    placeholder="Enter docket number"
                    data-testid="input-docket-number"
                    {...register("docketNumber")}
                  />
                  {errors.docketNumber && (
                    <p className="text-sm text-red-600">{errors.docketNumber.message}</p>
                  )}
                </div>
              </div>
              
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={handleFetchFMCSAData}
                disabled={!docketNumber || fetchFMCSADataMutation.isPending}
                data-testid="button-fetch-fmcsa-data"
              >
                {fetchFMCSADataMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Fetching FMCSA Data...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Fetch FMCSA Data
                  </>
                )}
              </Button>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Carrier"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
